import { Module, VuexModule, Action, Mutation } from 'vuex-module-decorators'
import AppStoreModuleContainer from './AppStoreModuleContainer'
import AppLock from './AppLock'
import AppError from './AppError'
import AppMessage from './AppMessage'

export interface PanicState {
  message: string
}

export interface AppStore {
  isPanic: boolean;
  panic: PanicState | null;
  asyncPanic(panic?: PanicState): Promise<void>;

  isWaiting: boolean;
  asyncLock(lock: AppLock): Promise<void>;
  asyncUnlock(lock: AppLock): Promise<void>;

  errors: AppError[];
  asyncPushError(error: AppError): Promise<void>;
  asyncDisposeError(error: AppError): Promise<void>;

  messages: AppMessage[];
  asyncPushMessage(message: AppMessage): Promise<void>;
  asyncDisposeMessage(message: AppMessage): Promise<void>;
}

@Module({
  name: 'app',
  namespaced: true,
  stateFactory: true
})
export default class AppStoreModule extends VuexModule implements AppStore {
  @Mutation
  inject (container: AppStoreModuleContainer) {
  }

  // Panic system
  private _panic: PanicState | null = null

  public get isPanic () {
    return this._panic !== null
  }

  public get panic () {
    return this._panic
  }

  @Mutation
  private setPanic (panic: PanicState) {
    this._panic = panic
  }

  @Action({ rawError: true })
  public async asyncPanic (panic?: PanicState): Promise<void> {
    if (panic === undefined) {
      panic = {
        message: 'panic.there_was_an_unknown_error'
      }
    }
    this.setPanic(panic)
  }

  // Transition lock system
  private _locks: AppLock[] = []

  get isWaiting () {
    return this._locks.length !== 0
  }

  @Mutation
  private addLock (lock: AppLock) {
    this._locks = this._locks.concat([lock])
  }

  @Mutation
  private removeLock (lock: AppLock) {
    this._locks = this._locks.filter((l) => {
      return lock.id !== l.id
    })
  }

  @Action({ rawError: true })
  public async asyncLock (lock: AppLock): Promise<void> {
    this.addLock(lock)
  }

  @Action({ rawError: true })
  public async asyncUnlock (lock: AppLock): Promise<void> {
    this.removeLock(lock)
  }

  // Error stream system
  private _errors: AppError[] = []

  public get errors () {
    return this._errors
  }

  @Mutation
  private pushError (error: AppError) {
    this._errors = this._errors.concat([error])
  }

  @Mutation
  private removeError (error: AppError) {
    this._errors = this._errors.filter((e) => {
      return e.id !== error.id
    })
  }

  @Action({ rawError: true })
  public async asyncPushError (error: AppError): Promise<void> {
    this.pushError(error)
    const message = new AppMessage()
    message.error = true
    if (error.error !== null && error.error.message !== '' && error.error.message !== undefined) {
      message.message = error.error.message
    } else {
      message.message = 'There was an error unexpected. Please retry later.'
    }
    await this.asyncPushMessage(message)
  }

  @Action({ rawError: true })
  public async asyncDisposeError (error: AppError): Promise<void> {
    this.removeError(error)
  }

  // Messages stream system
  private _messages: AppMessage[] = []

  public get messages () {
    return this._messages
  }

  @Mutation
  private pushMessage (message: AppMessage) {
    this._messages = this._messages.concat([message])
  }

  @Mutation
  private removeMessage (message: AppMessage) {
    this._messages = this._messages.filter((m) => {
      return m.id !== message.id
    })
  }

  @Action({ rawError: true })
  public async asyncPushMessage (message: AppMessage): Promise<void> {
    this.pushMessage(message)
    setTimeout(() => {
      this.asyncDisposeMessage(message)
    }, 5000)
  }

  @Action({ rawError: true })
  public async asyncDisposeMessage (message: AppMessage): Promise<void> {
    this.removeMessage(message)
  }
}
